import { TAXI_QUOTE_BRANDREF } from "../../../config/config";
import QuoteStep from "../../quote/enums/QuoteStep";
import useBuyStore from "../store/BuyStore";
import useAppStateStore from "../../../shared/store/AppStateStore";

const brandRef = TAXI_QUOTE_BRANDREF;

const handleErrorResponse = async (response, navigate) => {
  const responseData = await response.json();
  let errorObj = responseData.errors;
  const STATE = useAppStateStore.getState();
  if (responseData?.status == 500) {
    STATE.setCurrentStep(QuoteStep.TQSTART);
    navigate(`/error?message=Internal Server Error`);
    return;
  } else if (responseData?.status == 408) {
    STATE.setCurrentStep(QuoteStep.TQ_PANEL);
    navigate(`/error?message=Timed Out`);
    return;
  } else if (!errorObj || typeof errorObj !== "object") return "";

  let errorMessages = [];

  for (const key in errorObj) {
    // Skip the $ key
    if (key === "$") continue;

    // Check if the value is an array
    if (Array.isArray(errorObj[key])) {
      errorMessages = errorMessages.concat(errorObj[key]);
    }
  }

  // Join all error messages with a comma and return
  const message = errorMessages.join(", ");

  navigate(
    `/error?message=${encodeURIComponent(message) || "Internal Server Error"}`
  );

  throw new Error(message); // Throw an error to stop further processing
};

export const fetchOptional = async ({
  Answers,
  RiskObjectId,
  RiskObjectHistoryId,
  navigate,
}) => {
  const requestBody = {
    brandRef,
    RiskObjectId,
    RiskObjectHistoryId,
    UseSessionData: true,
  };

  try {
    const response = await fetch("/api/optional", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      await handleErrorResponse(response, navigate);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching optional:", error);
    throw error;
  }
};

export const fetchRecommendations = async ({
  Answers,
  RiskObjectId,
  RiskObjectHistoryId,
  navigate,
}) => {
  const requestBody = {
    brandRef,
    Answers,
    RiskObjectId,
    RiskObjectHistoryId,
    UseSessionData: true,
  };

  try {
    const response = await fetch("/api/recommendations", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      await handleErrorResponse(response, navigate);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching recommendations:", error);
    throw error;
  }
};

export const fetchPaymentOptions = async (requestBody, navigate) => {
  try {
    const response = await fetch("/api/payment/options", {
      method: "POST",
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      await handleErrorResponse(response, navigate);
    }
    const data = await response.json();

    useBuyStore.getState().setObject("paymentOptionsResponse", data);
    return data;
  } catch (error) {
    console.error("Error in fetchPaymentOptions: ", error);
    throw error; // Re-throw error to handle it in the calling component
  }
};

export const fetchPaymentJson = async (requestBody, navigate) => {
  try {
    const response = await fetch("/api/paymentReqJson", {
      method: "POST",
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      await handleErrorResponse(response, navigate);
    }

    return await response.json();
  } catch (error) {
    console.error("Error in fetchPaymentJson: ", error);
    throw error;
  }
};

export const convertQuoteIntoPolicy = async (quoteData, navigate) => {
  const url = "/api/ConvertQuoteIntopolicy";

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(quoteData),
    });

    if (!response.ok) {
      await handleErrorResponse(response, navigate);
    }

    return await response.json();
  } catch (error) {
    console.error("Error in convertQuoteIntoPolicy:", error);
    throw error;
  }
};

export const saveMember = async (requestBody, navigate) => {
  try {
    const response = await fetch("/api/save/member", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      await handleErrorResponse(response, navigate);
    }
    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error saving member:", error);
    throw error;
  }
};

export const fetchPclAgreementUrl = async (quoteData, navigate) => {
  const url = "/api/pclAgreementurl";

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(quoteData),
    });

    if (!response.ok) {
      await handleErrorResponse(response, navigate);
    }

    return await response.json();
  } catch (error) {
    console.error("Error in fetchPclAgreementUrl:", error);
    throw error;
  }
};

export async function getAddonsDescriptions() {
  try {
    // Fetch new data from the API
    const response = await fetch("/api/allAddons", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching addons descriptions:", error);
    throw error;
  }
}

export const fetchAddonsDescriptions = async () => {
  try {
    const data = await getAddonsDescriptions();
    const buyStore = useBuyStore.getState();
    buyStore.setObject("addonDescriptions", data);
  } catch (err) {
    console.log(err);
  }
};

export const fetchArchiveList = async (requestBody, navigate) => {
  try {
    const response = await fetch("/api/archive/list", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching addons descriptions:", error);
    throw error;
  }
};

export const fetchTemplateList = async (requestBody, navigate) => {
  try {
    const response = await fetch("/api/template/list", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching addons descriptions:", error);
    throw error;
  }
};

export const getDocumentDownloadURL = async (link, navigate) => {
  try {
    const response = await fetch(
      `/api/getDocumentDownloadURL?link=${encodeURIComponent(link)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    const data = await response.text();

    return data;
  } catch (error) {
    console.error("Error fetching addons descriptions:", error);
    throw error;
  }
};

export const getAddonDocumentDownloadURL = async (link, navigate) => {
  try {
    const response = await fetch(
      `/api/getDocumentAddonDownloadURL?link=${encodeURIComponent(link)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    const data = await response.text();

    return data;
  } catch (error) {
    console.error("Error fetching addons descriptions:", error);
    throw error;
  }
};
